import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text } from "tamagui";
import {
  RiAddLine,
  // TODO(account-hide): 以下 icon 對應已隱藏的帳戶概況/帳戶管理項目，待實作後恢復。詳見 docs/account-hidden-sections.md
  // RiCoinLine,
  // RiInboxLine,
  // RiErrorWarningLine,
  RiFileList3Line,
  RiBillLine,
  RiFileTextLine,
  RiCoupon3Line,
  // RiPriceTag3Line,
  // TODO(account-hide): RiBankCardLine 對應已隱藏的「付款方式」，待實作後恢復。詳見 docs/account-hidden-sections.md
  // RiBankCardLine,
  // RiSettings3Line,
  RiFireLine,
  RiEdit2Line,
  RiMapPin2Line,
  RiArrowRightSLine,
} from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { colors } from "@styles/colors";
import api from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { IdentityContext, useUser } from "@utils/store/user/useUser";
import { useOrder } from "@utils/store/cart/useOrder";
import AddressFormSheet, { combinedAddress } from "@components/address/AddressFormSheet";
import type { AddAddressFormData } from "@components/address/AddAddressFlowSheet";
import { createCustomerAddress, getCreateAddressErrorMessage } from "@screen/QuickOrder/utils/mockApi";
import { appToast } from "@components/Toast";

import { v2 } from "@screen/Home/pages/GasShop/skin";

import { handleAPIRenameContext } from "./API/handleContextAPI";
import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import FnRow from "./components/ui/FnRow";
import BottomSheet from "./components/ui/BottomSheet";
import { AddressRowsSkeleton, MemberChipsSkeleton } from "./components/ui/AccountSkeletons";
import { track } from "@lib/tracker";
// TODO(account-hide): StatBox / InfoNote 對應已隱藏的帳戶概況與財務提示，待實作後恢復。詳見 docs/account-hidden-sections.md
// import StatBox from "./components/ui/StatBox";
// import InfoNote from "./components/ui/InfoNote";
import PointsBalanceCard from "@components/points/PointsBalanceCard";
import { POINTS_ENABLED, usePointsSummary } from "@utils/points";
import { ORDER_ENTRY_PATH } from "@screen/Kiosk/orderEntry";

/** GET /app-api/customer/contexts/:context_id 回傳的明細（地址、權威成員在這支） */
interface ContextDetailAddress {
  customer_address_id: number | string;
  full_address: string;
  city?: string;
}
interface ContextDetailMember {
  customer_id: number;
  customer_name: string;
  role: string;
}
interface ContextDetailData {
  context_id: number;
  context_name: string;
  role?: "owner" | "member";
  member_count?: number;
  max_members?: number;
  primary_address?: ContextDetailAddress | null;
  primary_supplier?: { supplier_name: string } | null;
  members?: ContextDetailMember[];
}

interface MemberVM {
  name: string;
  isOwner: boolean;
  color?: string;
}

/** context 清單物件（router state）可能帶的本地地址 */
const getStateAddresses = (ctx: IdentityContext | null): { id: string; full: string }[] => {
  if (!ctx) return [];
  if (Array.isArray(ctx.address)) {
    return ctx.address.map((a) => ({ id: String(a.customer_address_id), full: a.full_address }));
  }
  if (ctx.choose_address) {
    return [{ id: String(ctx.choose_address.customer_address_id), full: ctx.choose_address.full_address }];
  }
  if (typeof ctx.address === "string") {
    return [{ id: "0", full: ctx.address }];
  }
  return [];
};

/**
 * 瓦斯行帳戶詳細頁（主頁）。
 * 先用 FamilyList 透過 router state 帶入的 IdentityContext 即時渲染，
 * 再以 GET /app-api/customer/contexts/:id 補上真實地址與權威成員。
 * 財務資料（點數/押桶/欠款/月結）明細未回 → 先佔位，之後接 API。
 */
export const AccountDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId } = useParams();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity, setChooseIdentity } = useUser();
  const { clearProducts } = useOrder();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const [detail, setDetail] = useState<ContextDetailData | null>(null);
  // context detail 載入中（成員區塊用；router state 沒帶成員時才需要骨架）
  const [detailLoading, setDetailLoading] = useState(true);
  const [openAddr, setOpenAddr] = useState(false);
  const [submittingAddr, setSubmittingAddr] = useState(false);
  // 本次新增的地址（立即顯示在清單，不必重抓 context detail）
  const [addedAddresses, setAddedAddresses] = useState<{ id: string; full: string }[]>([]);
  // 此帳戶可用的送貨地址（多筆）。context detail 只回一筆 primary_address，
  // 真正的完整清單在下單 bootstrap 的 available_addresses。
  const [ctxAddresses, setCtxAddresses] = useState<{ id: string; full: string }[]>([]);
  // 地址清單載入中 → 先畫骨架。詳見 AddressRowsSkeleton 的說明
  const [addrLoading, setAddrLoading] = useState(true);
  // 改名：本地覆寫顯示名稱（改完立即反映，免重抓 detail）+ BottomSheet 狀態
  const [nameOverride, setNameOverride] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [savingName, setSavingName] = useState(false);

  // 點數（存氣／帳戶）餘額。TODO(points-api): 目前為 mock，見 @utils/points/mockPoints。
  const { balance: pointsBalance, loading: pointsLoading } = usePointsSummary(contextId);

  // 新增地址：共用 AddressFormSheet（表單 + 地圖，無搜尋步驟）→ 建立到此帳戶
  const handleAddAddress = async (data: AddAddressFormData) => {
    if (submittingAddr) return;
    setSubmittingAddr(true);
    try {
      const res = await createCustomerAddress({
        addressInfos: {
          city: data.city || undefined,
          site_id: data.area || undefined,
          road: data.road || undefined,
          address_number: data.streetNumber?.trim() || undefined,
          floor: data.floor || undefined,
          room: data.room || undefined,
          extra: data.otherAddress || undefined,
          latitude: data.latitude ?? undefined,
          longitude: data.longitude ?? undefined,
        },
      });
      const display = combinedAddress({
        city: data.city || "",
        area: data.area || "",
        road: data.road || "",
        streetNumber: data.streetNumber,
        floor: data.floor,
        room: data.room,
      });
      setAddedAddresses((prev) => [
        ...prev,
        {
          id: String(res.customerAddressResult.customer_address_id),
          full: data.otherAddress?.trim() ? `${display} ${data.otherAddress}` : display,
        },
      ]);
      setOpenAddr(false);
      appToast.success("地址已新增");
    } catch (err) {
      throw new Error(getCreateAddressErrorMessage(err));
    } finally {
      setSubmittingAddr(false);
    }
  };

  useEffect(() => {
    if (!cusJwtToken || !contextId) {
      setDetailLoading(false);
      return;
    }
    let active = true;
    setDetailLoading(true);
    api
      .get(`/app-api/customer/contexts/${contextId}`, { headers: { Authorization: `Bearer ${cusJwtToken}` } })
      .then((res) => {
        if (active) setDetail((res.data?.data ?? null) as ContextDetailData | null);
      })
      .catch((err) => {
        // 靜默：保留 router state 的既有顯示
        console.log("context detail err", err);
      })
      .finally(() => {
        if (active) setDetailLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, contextId]);

  /* 送貨地址清單：context detail 的 primary_address 只有「主要地址」一筆，
     帳戶在後面新增的地址不會出現在那裡——卡片因此永遠停在舊的那筆。
     改抓下單 bootstrap 的 available_addresses（X-Context 指定此帳戶），
     與下單時地址切換選單同一份來源，兩邊看到的地址才會一致。 */
  useEffect(() => {
    if (!cusJwtToken || !contextId) {
      // 沒有身分或 context 就打不了這支——別讓骨架永遠轉下去
      setAddrLoading(false);
      return;
    }
    let active = true;
    setAddrLoading(true);
    api
      .get("/app-api/platform-order-drafts/bootstrap", {
        headers: { Authorization: `Bearer ${cusJwtToken}`, "X-Context": `context:${contextId}` },
      })
      .then((res) => {
        const list = res.data?.data?.available_addresses;
        if (!active || !Array.isArray(list)) return;
        setCtxAddresses(
          (list as ContextDetailAddress[])
            .filter((a) => a?.full_address)
            .map((a) => ({ id: String(a.customer_address_id), full: a.full_address }))
        );
      })
      .catch((err) => {
        // 靜默：退回 detail 的 primary_address / router state
        console.log("context addresses err", err);
      })
      .finally(() => {
        if (active) setAddrLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, contextId]);

  const back = () => navigate("/account");

  // 為此帳戶叫瓦斯：先切換為該帳戶身分，若已知綁定瓦斯行則直接進訂購頁（跳過瓦斯行列表）。
  // 沒有 primary_supplier（未綁定）才退回瓦斯行列表讓使用者選擇。
  const orderForAccount = () => {
    if (ctx && ctx.context_id !== chooseIdentity?.context_id) {
      setChooseIdentity(ctx);
      clearProducts();
    }
    track("entry_click", { entry_source: "account_detail" });
    const supplierId = ctx?.primary_supplier?.supplier_id;
    if (supplierId) {
      navigate(ORDER_ENTRY_PATH);
    } else {
      navigate("/gasShop/stations");
    }
  };

  if (!ctx && !detail) {
    return (
      <SkinV2Screen title="瓦斯行帳戶" onBack={back}>
        <YStack f={1} ai="center" jc="center" gap="$2">
          <Text fontSize={14} color={v2.sub}>
            載入中…
          </Text>
          <Text fontSize={12} color={v2.sub}>
            context {contextId ?? "-"}
          </Text>
        </YStack>
      </SkinV2Screen>
    );
  }

  const name = nameOverride || detail?.context_name || ctx?.label || ctx?.context_name || "瓦斯行帳戶";
  const role = ctx?.role ?? detail?.role;
  const isOwner = role === "owner";
  const vendor = ctx?.primary_supplier?.supplier_name ?? detail?.primary_supplier?.supplier_name ?? "未綁定瓦斯行";

  // 開啟改名視窗（帶入目前名稱；fallback 預設名則留空）
  const openRename = () => {
    setEditName(name === "瓦斯行帳戶" ? "" : name);
    setEditOpen(true);
  };

  // 儲存新名稱（owner-only；後端 PATCH /customer/contexts/:id）
  const handleRename = async () => {
    if (savingName) return;
    const trimmed = editName.trim();
    if (!trimmed) {
      appToast.error("請輸入帳戶名稱");
      return;
    }
    if (!cusJwtToken || !contextId) {
      appToast.error("請重新登入後再試");
      return;
    }
    if (trimmed === name) {
      setEditOpen(false);
      return;
    }
    setSavingName(true);
    const result = await handleAPIRenameContext(contextId, trimmed, cusJwtToken);
    setSavingName(false);
    if (result.state !== "success") {
      appToast.error(result.error || "更新失敗");
      return;
    }
    const newName = result.data?.context_name ?? trimmed;
    setNameOverride(newName);
    setDetail((prev) => (prev ? { ...prev, context_name: newName } : prev));
    // 同步當前選擇身分，讓切換選單與其他頁立即反映新名稱
    if (chooseIdentity && String(chooseIdentity.context_id) === String(contextId)) {
      setChooseIdentity({ ...chooseIdentity, context_name: newName, label: undefined });
    }
    setEditOpen(false);
    appToast.success("帳戶名稱已更新");
  };

  const members: MemberVM[] = (detail?.members
    ? detail.members.map((m) => ({ name: m.customer_name, isOwner: m.role === "owner" }))
    : (ctx?.members ?? []).map((m) => ({ name: m.customer_name, isOwner: false, color: m.color }))) as MemberVM[];
  const memberCount = detail?.member_count ?? ctx?.member_count ?? members.length;
  const isShared = memberCount > 1;
  /* 成員骨架只在「手上真的沒東西可顯示」時出現。
     router state 帶來的成員跟 detail 是同一份後端資料（清單頁剛拿的），
     顯示它不會被推翻——這點跟地址不同：地址的 primary_address 明知只有一筆、
     一定會被完整清單改寫，所以那邊即使有值也先畫骨架。 */
  const membersLoading = detailLoading && members.length === 0;

  // 有 bootstrap 的完整清單就用它；拿不到（載入中／失敗）才退回單筆 primary_address 或 router state
  const baseAddresses = ctxAddresses.length
    ? ctxAddresses
    : detail?.primary_address
      ? [{ id: String(detail.primary_address.customer_address_id), full: detail.primary_address.full_address }]
      : getStateAddresses(ctx);
  // 併入本次新增的（去重）
  const addresses = [...baseAddresses, ...addedAddresses.filter((a) => !baseAddresses.some((b) => b.id === a.id))];

  /* 淡霧頁首上的大標：帳戶名稱、共享徽章、瓦斯行與角色。
     放在 subHeader（跟著內容捲走）而不是釘住的標題列裡——名稱是這頁的主體，
     釘住的那條只留「瓦斯行帳戶」四個字，捲動時高度不變才不會重排。 */
  const subHeader = (
    <YStack mb={16}>
      <XStack ai="center" gap="$2" fw="wrap" mb="$1.5">
        <Text color={v2.hazeInk} fontSize={22} fontWeight="800">
          {name}
        </Text>
        {isOwner && (
          <View
            ai="center"
            jc="center"
            w={28}
            h={28}
            br={14}
            bg={v2.primarySoft}
            cursor="pointer"
            hoverStyle={{ bg: "#DCEBFA" }}
            onPress={openRename}>
            <RiEdit2Line size={14} color={v2.hazeInk} />
          </View>
        )}
        {isShared && (
          <View br={12} px="$2.5" py="$1" bg="#fff" bw={1} bc={v2.cardBorder}>
            <Text color={v2.hazeInk} fontSize={11} fontWeight="700">
              群組共享 · {memberCount} 人
            </Text>
          </View>
        )}
      </XStack>
      <XStack ai="center" gap="$2" fw="wrap">
        <Text color={v2.hazeSub} fontSize={12}>
          {vendor}
        </Text>
        {role && (
          <>
            <Text color={v2.disabled} fontSize={12}>
              ·
            </Text>
            <Text color={v2.hazeSub} fontSize={12}>
              {role === "owner" ? "你是負責人" : "你是成員"}
            </Text>
          </>
        )}
      </XStack>
    </YStack>
  );

  return (
    <SkinV2Screen title="瓦斯行帳戶" onBack={back} subHeader={subHeader}>
      <YStack className="gas-stagger-kids" gap="$4">
        {/* TODO(account-hide): 帳戶概況（瓦斯點數/押桶/欠款）暫時隱藏，待實作後恢復。詳見 docs/account-hidden-sections.md */}
        {/* <YStack>
          <SectionLabel title="帳戶概況" right={<Text fontSize={10} color={colors.hint}>限 {vendor} 使用</Text>} />
          <XStack gap="$2">
            <View f={1}>
              <StatBox label="瓦斯點數" value="—" sub="待接 API" icon={<RiCoinLine size={12} color={colors.amber} />} />
            </View>
            <View f={1}>
              <StatBox label="押桶" value="—" sub="待接 API" icon={<RiInboxLine size={12} color={colors.sub} />} />
            </View>
            <View f={1}>
              <StatBox label="欠款" value="—" sub="待接 API" icon={<RiErrorWarningLine size={12} color={colors.green} />} />
            </View>
          </XStack>
        </YStack> */}

        {/* 點數：存氣（退桶剩餘氣）與帳戶（溢繳／儲值）。兩者都由這家瓦斯行認列，
            所以放在帳戶頁而不是帳號首頁 —— 換一家瓦斯行就是另一份餘額。 */}
        {POINTS_ENABLED && (
          <YStack>
            <SectionLabel
              tone="dark"
              title="點數"
              right={
                <Text fontSize={10} color={v2.hazeSub}>
                  限 {vendor} 使用
                </Text>
              }
            />
            <XStack gap="$2.5">
              <View f={1}>
                <PointsBalanceCard
                  kind="stored_gas"
                  points={pointsBalance.storedGas}
                  loading={pointsLoading}
                  onPress={() => navigate(`/account/detail/${contextId}/points/stored-gas`, { state: { context: ctx } })}
                />
              </View>
              <View f={1}>
                <PointsBalanceCard
                  kind="account"
                  points={pointsBalance.account}
                  loading={pointsLoading}
                  onPress={() => navigate(`/account/detail/${contextId}/points/account`, { state: { context: ctx } })}
                />
              </View>
            </XStack>
          </YStack>
        )}

        {/* 成員 */}
        <YStack>
          {/* 頁首是淡霧底（見 useHazePage），區塊標題用一般深色即可 */}
          <SectionLabel
            tone="dark"
            title={membersLoading ? "成員" : `成員 (${memberCount})`}
            right={
              // 淡霧底：白字等於消失。這是一個動作（不是說明文字），用品牌藍才看得出可點
              <XStack ai="center" gap="$1" cursor="pointer" onPress={() => navigate(`/account/detail/${contextId}/invite`, { state: { context: ctx } })}>
                <RiAddLine size={12} color={v2.hazeAcc} />
                <Text fontSize={12} fontWeight="700" color={v2.hazeAcc}>
                  邀請成員
                </Text>
              </XStack>
            }
          />
          {/* 成員用 chips 平鋪（人多時最省版面、一眼看得完）。
              卡片本身不再是整塊可點——那樣沒有任何箭頭，客戶不知道會進成員權限頁；
              入口獨立成底下那條帶箭頭的列。 */}
          <ListContainer>
            {membersLoading ? (
              <MemberChipsSkeleton />
            ) : members.length === 0 ? (
              <Text fontSize={13} color={v2.sub} ta="center" py="$5">
                尚無成員資料
              </Text>
            ) : (
              <XStack fw="wrap" gap="$2" px="$3.5" pt="$3.5" pb="$3">
                {members.map((m, i) => (
                  <XStack key={i} ai="center" gap="$2" bg={v2.chipBg} br={999} pl="$1.5" pr="$3" py="$1.5">
                    <View w={26} h={26} br={13} bg={`${m.color ?? colors.primary}26`} ai="center" jc="center">
                      <Text fontSize={12} fontWeight="800" color={m.color ?? colors.primary}>
                        {m.name.charAt(0)}
                      </Text>
                    </View>
                    <Text fontSize={13} fontWeight="700" color={v2.title}>
                      {m.name}
                    </Text>
                    {m.isOwner && (
                      <Text fontSize={10} fontWeight="700" color={colors.amber}>
                        負責人
                      </Text>
                    )}
                  </XStack>
                ))}
              </XStack>
            )}
            <XStack
              ai="center"
              jc="center"
              gap="$1"
              py="$3"
              btw={1}
              btc={v2.divider}
              cursor="pointer"
              pressStyle={{ opacity: 0.6 }}
              onPress={() => navigate(`/account/detail/${contextId}/members`, { state: { context: ctx } })}>
              <Text fontSize={13} fontWeight="700" color={v2.primary}>
                管理成員權限
              </Text>
              <RiArrowRightSLine size={16} color={v2.primary} />
            </XStack>
          </ListContainer>
        </YStack>

        {/* 送貨地址 */}
        <YStack>
          {/* 載入中不給數字：先寫「(1)」等一下又變「(2)」，比沒有數字更擾人 */}
          <SectionLabel title={addrLoading ? "送貨地址" : `送貨地址 (${addresses.length})`} />
          <ListContainer>
            {addrLoading ? (
              <AddressRowsSkeleton />
            ) : addresses.length === 0 ? (
              <Text fontSize={13} color={v2.sub} ta="center" py="$6">
                尚無送貨地址
              </Text>
            ) : (
              // 地址列只放裸 icon，不做 FnRow 那種淺底方塊：這裡一列就是一筆資料、不是可點的功能入口，
              // 加上底色會讓它看起來跟「帳戶管理」那些列同級。
              addresses.map((ad, i) => (
                <XStack key={ad.id} ai="center" gap="$2" px="$3.5" py="$3" bbw={1} bbc={v2.divider}>
                  <View w={18} ai="center" jc="center" flexShrink={0}>
                    <RiMapPin2Line size={17} color={colors.primary} />
                  </View>
                  <Text f={1} fontSize={13.5} color={v2.body} numberOfLines={2}>
                    {ad.full}
                  </Text>
                </XStack>
              ))
            )}
            <XStack
              ai="center"
              jc="center"
              gap="$1.5"
              py="$3"
              cursor="pointer"
              pressStyle={{ opacity: 0.6 }}
              // 改用共用新增地址 modal（AddressFormSheet：表單 + 地圖，無搜尋）。
              // 舊邏輯（導向 AddressEdit 切版頁）先註解保留：
              // onPress={() => navigate(`/account/detail/${contextId}/address/new`, { state: { context: ctx } })}>
              onPress={() => setOpenAddr(true)}>
              <RiAddLine size={15} color={v2.primary} />
              <Text fontSize={13} fontWeight="700" color={v2.primary}>
                新增地址
              </Text>
            </XStack>
          </ListContainer>
        </YStack>

        {/* 帳戶管理 */}
        <YStack>
          <SectionLabel title="帳戶管理" />
          <ListContainer>
            <FnRow
              icon={<RiFileList3Line size={15} color={colors.primary} />}
              iconColor={colors.primary}
              label="訂單紀錄"
              sub="此帳戶的叫瓦斯訂單"
              onPress={() => navigate(`/account/detail/${contextId}/orders`, { state: { context: ctx } })}
            />
            <FnRow
              icon={<RiBillLine size={15} color={colors.amber} />}
              iconColor={colors.amber}
              label="付款紀錄"
              sub="此帳戶的付款紀錄與每月小計"
              onPress={() => navigate(`/account/detail/${contextId}/payment-records`, { state: { context: ctx } })}
            />
            <FnRow
              icon={<RiFileTextLine size={15} color={colors.purple} />}
              iconColor={colors.purple}
              label="帳單"
              sub="瓦斯行開立的月結、瓦斯表帳單與繳費資訊"
              onPress={() => navigate(`/account/detail/${contextId}/bills`, { state: { context: ctx } })}
            />
            {/* WORK B：優惠券歸屬此帳戶 — 只顯示平台券 + 此帳戶 supplier 的帳戶券 */}
            <FnRow
              icon={<RiCoupon3Line size={15} color={colors.green} />}
              iconColor={colors.green}
              label="優惠券"
              sub="此帳戶可用的平台券與帳戶券"
              isLast
              // 帶 from：優惠券頁的返回要回到這個帳戶，而不是退回帳號首頁
              onPress={() => navigate(`/account/coupons`, { state: { context: ctx, from: `/account/detail/${contextId}` } })}
            />
            {/* TODO(account-hide): 付款方式暫時隱藏，待實作後恢復。詳見 docs/account-hidden-sections.md */}
            {/* <FnRow
              icon={<RiBankCardLine size={15} color={colors.purple} />}
              iconColor={colors.purple}
              label="付款方式"
              sub="管理此帳戶可用的支付工具"
              isLast
              onPress={() => navigate(`/account/detail/${contextId}/payments`, { state: { context: ctx } })}
            /> */}
            {/* TODO(account-hide): 定價表 / 帳戶設定 暫時隱藏，待實作後恢復。詳見 docs/account-hidden-sections.md */}
            {/* <FnRow
              icon={<RiPriceTag3Line size={15} color={colors.amber} />}
              iconColor={colors.amber}
              label="定價表"
              sub={`查看 ${vendor} 的桶裝瓦斯定價`}
              onPress={() => navigate(`/account/detail/${contextId}/pricing`, { state: { context: ctx } })}
            />
            <FnRow
              icon={<RiSettings3Line size={15} color={colors.sub} />}
              iconColor={colors.sub}
              label="帳戶設定"
              sub="名稱、解除綁定"
              isLast
              onPress={() => navigate(`/account/detail/${contextId}/settings`, { state: { context: ctx } })}
            /> */}
          </ListContainer>
        </YStack>

        {/* TODO(account-hide): 財務資訊提示暫時隱藏，待實作後恢復。詳見 docs/account-hidden-sections.md */}
        {/* <InfoNote tone="primary">
          財務資訊（瓦斯點數、押桶、欠款、月結）將於串接帳戶 API 後顯示。
        </InfoNote> */}

        {/* 叫瓦斯 CTA。V2 的主要動作是實色膠囊，不是漸層方塊——
            漸層在白卡林立的版面裡會變成第二個視覺重心。 */}
        <XStack
          backgroundColor={v2.primary}
          ai="center"
          jc="center"
          gap="$2"
          br={999}
          py={15}
          mt="$1"
          cursor="pointer"
          pressStyle={{ opacity: 0.85 }}
          onPress={orderForAccount}>
          <RiFireLine size={19} color={colors.light} />
          <Text fontSize={15} fontWeight="800" color={colors.light}>
            為「{name}」叫瓦斯
          </Text>
        </XStack>
      </YStack>

      {/* 新增地址：共用 AddressFormSheet（表單 + 地圖，無搜尋步驟） */}
      <AddressFormSheet isOpen={openAddr} onOpenChange={setOpenAddr} onSelect={handleAddAddress} />

      {/* 改名：帳戶名稱編輯（owner-only） */}
      <BottomSheet open={editOpen} onClose={() => setEditOpen(false)}>
        <YStack px="$4" pb="$5" gap="$3.5">
          <Text fontSize={17} fontWeight="800" color={colors.text}>
            修改帳戶名稱
          </Text>
          <YStack gap="$2">
            <Text fontSize={12} color={colors.sub}>
              帳戶名稱
            </Text>
            <View br={10} bw={1} bc={colors.border} px="$3" py="$2.5" bg={colors.bg}>
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="輸入帳戶名稱"
                maxLength={50}
                autoFocus
                style={{
                  width: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 15,
                  fontWeight: 600,
                  color: colors.text,
                }}
              />
            </View>
          </YStack>
          <YStack gap="$2.5" mt="$1">
            <XStack
              className="primary-bg-gradient"
              ai="center"
              jc="center"
              br={14}
              py="$3.5"
              cursor={savingName ? "not-allowed" : "pointer"}
              opacity={savingName ? 0.6 : 1}
              onPress={handleRename}>
              <Text fontSize={14} fontWeight="700" color={colors.light}>
                {savingName ? "儲存中…" : "儲存"}
              </Text>
            </XStack>
            <XStack
              ai="center"
              jc="center"
              br={14}
              py="$3.5"
              bg={colors.bg}
              bw={1}
              bc={colors.border}
              cursor="pointer"
              onPress={() => setEditOpen(false)}>
              <Text fontSize={14} fontWeight="600" color={colors.sub}>
                取消
              </Text>
            </XStack>
          </YStack>
        </YStack>
      </BottomSheet>
    </SkinV2Screen>
  );
};

export default AccountDetail;
